import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import {
  CustomTemplateValue,
  ListTemplatesRequestSearchParams,
  TemplateCloneRequest,
  TemplateCreateRequest,
  TemplateLookup,
  TemplateUpdateRequest,
  TemplateValue,
} from '../../models/TemplateLibrary'
import { BaseRequest, BaseResponse } from '../models'
import { createApi } from '@reduxjs/toolkit/query/react'
import { paginate } from '../utils'

const _BASE_URL = import.meta.env.REACT_APP_AI_BASE_URL!

export interface CreateTemplateRequest extends BaseRequest {
  request: TemplateCreateRequest
}

export interface TemplateRequest extends BaseRequest {
  templateId: string
}

export interface CloneTemplateRequest extends TemplateRequest {
  request: TemplateCloneRequest
}

export interface ListTemplatesRequest extends BaseRequest, ListTemplatesRequestSearchParams {}

export interface UpdateTemplateRequest extends TemplateRequest {
  request: TemplateUpdateRequest
}

export interface TemplateReferenceItem {
  id: string
  name: string
  kind: 'meeting_template'
}

export interface TemplateReferences {
  count: number
  items?: TemplateReferenceItem[]
}

export interface DeleteTemplateRequest extends TemplateRequest {
  replaceWithTemplateId?: string | null
}

export const templateLibraryApi = createApi({
  reducerPath: 'templateLibraryApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['TemplateLibrary'],
  endpoints: (builder) => ({
    createTemplate: builder.mutation<CustomTemplateValue, CreateTemplateRequest>({
      query: ({ accountId, request }) => ({
        url: `/accounts/${accountId}/template-library`,
        method: 'POST',
        body: request,
      }),
      transformResponse: (response: { data: CustomTemplateValue }) => response.data,
      invalidatesTags: [{ type: 'TemplateLibrary', id: 'LIST' }],
    }),
    getTemplate: builder.query<TemplateValue, TemplateRequest>({
      query: ({ accountId, templateId }) => ({
        url: `/accounts/${accountId}/template-library/${templateId}`,
      }),
      transformResponse: (response: { data: TemplateValue }) => response.data,
      providesTags: (_, __, { templateId }) => [{ type: 'TemplateLibrary', id: templateId }],
    }),
    listTemplates: builder.query<BaseResponse<TemplateLookup[]>, ListTemplatesRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<TemplateValue>(
          {
            url: `/accounts/${request.accountId}/template-library`,
            params: {
              ...(request.type && { type: request.type }),
              ...(request.typeId && { typeId: request.typeId }),
              ...(request.defaultTypeId && { defaultTypeId: request.defaultTypeId }),
              ...(request.createdBy && { createdBy: request.createdBy }),
              ...(request.scopes && { scopes: request.scopes.join(',') }),
            },
          },
          fetchWithBQ,
        )
      },
      providesTags: (result) =>
        result
          ? [...result.data.map(({ id }) => ({ type: 'TemplateLibrary' as const, id })), { type: 'TemplateLibrary', id: 'LIST' }]
          : [{ type: 'TemplateLibrary', id: 'LIST' }],
    }),
    cloneTemplate: builder.mutation<CustomTemplateValue, CloneTemplateRequest>({
      query: ({ accountId, templateId, request }) => ({
        url: `/accounts/${accountId}/template-library/${templateId}/clone`,
        method: 'POST',
        body: request,
      }),
      transformResponse: (response: { data: CustomTemplateValue }) => response.data,
      invalidatesTags: [{ type: 'TemplateLibrary', id: 'LIST' }],
    }),
    updateTemplate: builder.mutation<CustomTemplateValue, UpdateTemplateRequest>({
      query: ({ accountId, templateId, request }) => ({
        url: `/accounts/${accountId}/template-library/${templateId}`,
        method: 'PATCH',
        body: request,
      }),
      transformResponse: (response: { data: CustomTemplateValue }) => response.data,
      invalidatesTags: (_, __, { templateId }) => [
        { type: 'TemplateLibrary', id: templateId },
        { type: 'TemplateLibrary', id: 'LIST' },
      ],
    }),
    deleteTemplate: builder.mutation<void, DeleteTemplateRequest>({
      query: ({ accountId, templateId, replaceWithTemplateId }) => ({
        url: `/accounts/${accountId}/template-library/${templateId}`,
        method: 'DELETE',
        body: replaceWithTemplateId ? { replaceWithTemplateId } : undefined,
      }),
      invalidatesTags: [{ type: 'TemplateLibrary', id: 'LIST' }],
    }),
    getTemplateReferences: builder.query<TemplateReferences, TemplateRequest>({
      query: ({ accountId, templateId }) => ({
        url: `/accounts/${accountId}/template-library/${templateId}/references`,
      }),
      transformResponse: (response: { data: TemplateReferences }) => response.data,
    }),
  }),
})

export const {
  useGetTemplateQuery,
  useListTemplatesQuery,
  useCreateTemplateMutation,
  useCloneTemplateMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,
  useGetTemplateReferencesQuery,
} = templateLibraryApi
